import React, { InputHTMLAttributes, ReactNode, useImperativeHandle, useRef } from 'react';
import { useInputField } from '../../../../../utils';
import { controllableInputElementsProps } from '../../../index';
import { StyledInvalidMessage } from '../../invalid-message';
import { StyledHelpText } from '../../help-text';
import styles from './switch-element.module.scss';
import clsx from 'clsx';

export interface SwitchElementProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'placeholder'>,
        controllableInputElementsProps {
    hideInvalidMessage?: boolean;
    title?: string;
    // Trailing content beside the switch (e.g. count badge + clear button); outside <label> so its clicks don't toggle it.
    chip?: ReactNode;
}

export const SwitchElement = React.forwardRef<HTMLInputElement, SwitchElementProps>(
    (
        {
            label,
            helpText,
            title,
            isInvalid,
            invalidMessage,
            id,
            hideInvalidMessage,
            className,
            chip,
            ...rest
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement | null>(null);
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [ref]);

        const { fieldId, helpTextId, describedById, showHelpText } = useInputField({
            id,
            helpText,
            isInvalid,
            invalidMessage,
        });

        const showInvalidMessage = invalidMessage && !hideInvalidMessage;

        return (
            <fieldset className={styles.fieldset} disabled={rest.disabled}>
                <div className={styles.row}>
                    <label className={styles.wrapper} key={fieldId} htmlFor={fieldId}>
                        {(label || showHelpText) && (
                            <span className={clsx(styles.textColumn, 'switch-element-text')}>
                                {label && (
                                    <legend
                                        className={clsx(styles.legend, {
                                            [styles.invalid]: isInvalid,
                                            [styles.valid]: !isInvalid,
                                            [styles.disabled]: rest?.disabled,
                                        })}
                                        title={title}
                                    >
                                        {label}
                                    </legend>
                                )}
                                {showHelpText && (
                                    <StyledHelpText
                                        id={helpTextId}
                                        className={styles.helpText}
                                        children={helpText}
                                    />
                                )}
                            </span>
                        )}

                        <input
                            {...rest}
                            type="checkbox"
                            id={fieldId}
                            ref={inputRef}
                            aria-describedby={describedById}
                            className={clsx(styles.input, className)}
                        />

                        <span className={clsx(styles.switchIndicator, 'switch-element-track')} />
                    </label>

                    {chip && <div className={styles.chipSlot}>{chip}</div>}
                </div>
                {showInvalidMessage && <StyledInvalidMessage children={invalidMessage} />}
            </fieldset>
        );
    },
);

export default SwitchElement;
